import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useFeature } from '../hooks/useFeature';
import { useTenant } from '../contexts/TenantContext';
import { FEATURE_BY_SLUG } from '../lib/featureCatalog';

/**
 * FeatureGuard — protège une route derrière un slug de feature.
 *
 * Usage :
 *   <Route
 *     path="/admin/cashback"
 *     element={
 *       <FeatureGuard feature="pages.cashback.management">
 *         <CashbackManagement />
 *       </FeatureGuard>
 *     }
 *   />
 *
 * Comportement :
 *  - Feature ON → rend `children`
 *  - Feature OFF → redirige vers `redirectTo` (défaut /admin/dashboard)
 *  - Feature inconnue dans le catalogue → laisse passer (avec un warning)
 *    pour éviter de bloquer une page non-cataloguée par erreur.
 *  - Tant que `tenant` est en cours de chargement, on rend `fallback` (skeleton)
 *    pour éviter une redirection prématurée.
 *
 * Props :
 *  - feature   : slug requis (string, ex: 'pages.creators.list_admin')
 *  - children  : JSX à protéger
 *  - redirectTo: route de fallback si feature OFF (default '/admin/dashboard')
 *  - fallback  : JSX rendu pendant le chargement du tenant
 *  - roles     : (optionnel) liste de rôles autorisés. Si fournie, l'utilisateur
 *                doit avoir un rôle dans cette liste **en plus** d'avoir la
 *                feature activée. Utile pour double-gating (feature + rôle).
 */
const FeatureGuard = ({
  feature,
  children,
  redirectTo = '/admin/dashboard',
  fallback = null,
  roles = null,
}) => {
  const enabled = useFeature(feature);
  const { loading, userRole } = useTenant();
  const location = useLocation();

  if (loading) {
    return fallback ?? <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }

  if (!FEATURE_BY_SLUG[feature]) {
    if (typeof window !== 'undefined' && window.console) {
      console.warn(`[FeatureGuard] Unknown feature slug "${feature}" — letting through. Add it to src/lib/featureCatalog.js.`);
    }
    return children;
  }

  if (!enabled) {
    return <Navigate to={redirectTo} replace state={{ from: location.pathname, blockedBy: feature }} />;
  }

  if (roles && roles.length > 0) {
    if (!userRole || !roles.includes(userRole)) {
      return <Navigate to="/" replace state={{ from: location.pathname, deniedRole: userRole }} />;
    }
  }

  return children;
};

export default FeatureGuard;
